import { useState, useEffect } from "react";
import { Bell, X, Info, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle
};

const typeColors = {
  info: "text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/30",
  success: "text-[#0ECB81] bg-[#0ECB81]/10 border-[#0ECB81]/30",
  warning: "text-[#F0B90B] bg-[#F0B90B]/10 border-[#F0B90B]/30",
  danger: "text-[#F6465D] bg-[#F6465D]/10 border-[#F6465D]/30"
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? {...n, read: true} : n)
      );
    } catch (e) {
      console.error(e);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="relative">
      <Button 
        variant="ghost" 
        size="sm"
        className="relative text-[#848E9C] hover:text-[#EAECEF]"
        onClick={() => setShowPanel(!showPanel)}
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#F6465D] text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {/* Notification Panel */}
      {showPanel && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setShowPanel(false)}
          />
          
          {/* Panel */}
          <Card className="absolute right-0 top-12 w-80 sm:w-96 z-50 card-dark border border-[#2B3139] shadow-2xl max-h-[70vh] overflow-hidden">
            <CardHeader className="border-b border-[#2B3139] py-3 px-4 flex flex-row items-center justify-between">
              <CardTitle className="text-[#EAECEF] text-base flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#FCD535]" />
                Notificaciones
                {unreadCount > 0 && (
                  <Badge className="bg-[#F6465D] text-white text-xs">{unreadCount}</Badge>
                )}
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-[#848E9C] hover:text-[#EAECEF] -mr-2"
                onClick={() => setShowPanel(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-0 overflow-y-auto max-h-[calc(70vh-60px)]">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-[#848E9C]">
                  <Bell className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No tienes notificaciones</p>
                </div>
              ) : (
                <div className="divide-y divide-[#2B3139]">
                  {notifications.map((notification) => {
                    const Icon = typeIcons[notification.type] || Info;
                    const colorClass = typeColors[notification.type] || typeColors.info;
                    
                    return (
                      <div 
                        key={notification.id}
                        className={`p-4 hover:bg-[#1E2329] transition-colors cursor-pointer ${!notification.read ? 'bg-[#181A20]' : ''}`}
                        onClick={() => !notification.read && markAsRead(notification.id)}
                      >
                        <div className="flex gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${colorClass}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`font-medium text-sm ${!notification.read ? 'text-[#EAECEF]' : 'text-[#848E9C]'}`}>
                                {notification.title}
                              </p>
                              {!notification.read && (
                                <span className="w-2 h-2 bg-[#F6465D] rounded-full flex-shrink-0 mt-1.5" />
                              )}
                            </div>
                            <p className="text-xs text-[#848E9C] mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                            <p className="text-xs text-[#5E6673] mt-1">
                              {new Date(notification.created_at).toLocaleDateString()} {new Date(notification.created_at).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
